import { Suspense, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import {
  BreadcrumbJsonLd,
  BlogPostingJsonLd,
  FAQPageJsonLd,
  OrganizationJsonLd,
} from '@/components/seo/JsonLd';
import { VisibleFaqSection } from "@/components/seo/VisibleFaqSection";
import { resolveFaqsForPathname } from '@/lib/faqResolver';
import { persistManualLanguage, type AppLanguage } from '@/lib/languageResolution';
import { buildHeadMeta } from '@/lib/headMeta';
import { SEO_LANGUAGES, normalizeSeoLanguage } from '@/lib/seoMetadata';
import { classifyUrlLanguagePrefix, normalizeUrlPath } from '@/lib/urlNormalise';
import { ensureLanguageResources } from '@/i18n';
import { useContactForm } from '@/components/ContactFormModal';
import { RenderErrorBoundary } from '@/components/RenderErrorBoundary';

const SUPPORTED_LANGS = [...SEO_LANGUAGES];

// The latest-request latch shared by the rescue effect and the switcher
// (CWS-199): a superseded request abandons itself at its continuation, so
// rapid flag clicks land in click order, not fetch-completion order — what
// the previous synchronous code guaranteed implicitly.
let latestLanguageRequest = 0;

/**
 * Chain ensure → latch → changeLanguage for one language request
 *
 * Shared by the rescue effect and ``useLanguageNavigation``. An
 * already-registered locale takes the synchronous fast path — change,
 * continuation, done — byte-identical to the pre-CWS-199 code in both
 * callstack and ordering, which is what the deep-link render and the
 * existing journey tests rely on. Only a not-yet-registered locale pays the
 * async chain, and there the rules are strict: the language changes only
 * once its payload is registered (the FAQ resolver throws for an
 * unregistered language; since CWS-5 the router-level boundary contains
 * that throw, but the refusal still spares the visitor a full-page
 * fallback), a request a newer one has superseded abandons itself, and a failed
 * ensure logs and changes nothing — the visitor stays on the current
 * language, which is strictly better than trading the whole page for the
 * router fallback a "switch anyway" would produce (CWS-5).
 *
 * Args:
 *     i18n: The i18next instance (from ``useTranslation`` — the singleton
 *         the provider and the loader both configure).
 *     lang: The language to switch to.
 *     onChange: The continuation to run when this request wins and the
 *         language is registered.
 */
function requestLanguageChange(
  i18n: I18nInstance,
  lang: string,
  onChange: () => void,
): void {
  if (i18n.hasResourceBundle(normalizeSeoLanguage(lang), "translation")) {
    // Invalidate any pending async request: this switch has already won.
    latestLanguageRequest += 1;
    void i18n.changeLanguage(lang);
    onChange();
    return;
  }
  const token = ++latestLanguageRequest;
  ensureLanguageResources(lang)
    .then(() => {
      if (token !== latestLanguageRequest) {
        return;
      }
      return i18n.changeLanguage(lang);
    })
    .then(() => {
      if (token !== latestLanguageRequest) {
        return;
      }
      onChange();
    })
    .catch((error) => {
      console.error(
        `[locale-loader] switch to ${lang} failed; staying on the current language`,
        error,
      );
    });
}

export function LanguageRouter() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // One prefix contract for the whole module (CWS-62): the classifier owns
    // what the first segment means and what stripping it leaves, so this
    // effect and `changeLanguage` cannot drift apart again.
    const prefix = classifyUrlLanguagePrefix(location.pathname, SUPPORTED_LANGS);

    if (prefix.kind === 'supported') {
      if (i18n.language !== prefix.language) {
        // CWS-199: ensure-before-change, with the shared latch. Fire-and-
        // forget, as before — a superseded request abandons itself, a failed
        // one logs and leaves the page on its current language.
        requestLanguageChange(i18n, prefix.language, () => {});
      }
    } else {
      const currentLang = i18n.language?.split('-')[0] || 'en';
      const lang = SUPPORTED_LANGS.includes(currentLang as AppLanguage) ? currentLang : 'en';
      const newPath = `/${lang}${prefix.pathWithoutPrefix}${location.search}${location.hash}`;
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, i18n.language, navigate, location.search, location.hash]);

  // Set all SEO meta tags based on current route and language
  useEffect(() => {
    // Every value below comes from the module the build-time generator also
    // calls, so the crawler's <head> and this one cannot drift apart (B5).
    // This effect owns only how the tags are written, never what they say --
    // including the CWS-128 split between the title brand and the legal name.
    const meta = buildHeadMeta({
      pathname: location.pathname,
      copy: {
        homeTitle: t('meta.homeTitle'),
        homeDescription: t('meta.homeDescription'),
        pages: t('meta.pages', { returnObjects: true }) as Record<
          string,
          { title: string; description: string }
        >,
      },
    });

    // Document title
    document.title = meta.title;

    // HTML lang attribute
    document.documentElement.lang = meta.lang;

    // Standard meta
    setMeta('description', meta.description);

    // Canonical
    setLink('canonical', meta.canonicalUrl);

    // Open Graph
    setMeta('og:title', meta.title, true);
    setMeta('og:description', meta.description, true);
    setMeta('og:url', meta.canonicalUrl, true);
    setMeta('og:image', meta.ogImageUrl, true);
    setMeta('og:image:width', String(meta.ogImageDimensions.width), true);
    setMeta('og:image:height', String(meta.ogImageDimensions.height), true);
    setMeta('og:locale', meta.ogLocale, true);
    replaceMetaList('og:locale:alternate', meta.alternateOgLocales, 'property');
    setMeta('og:type', meta.ogType, true);
    setMeta('og:site_name', meta.siteName, true);

    // Twitter Card
    setMeta('twitter:card', meta.twitterCard, false, 'name');
    setMeta('twitter:title', meta.title, false, 'name');
    setMeta('twitter:description', meta.description, false, 'name');
    setMeta('twitter:image', meta.ogImageUrl, false, 'name');

    // Hreflang alternate links for all supported languages, plus x-default
    for (const alternate of meta.alternates) {
      setLink('alternate', alternate.href, alternate.hreflang);
    }
  }, [location.pathname, i18n.language, t]);

  // Detect blog post slug for BlogPosting JSON-LD
  const pathParts = location.pathname.split('/').filter(Boolean);
  const routePath = pathParts.slice(1).join('/');
  const blogSlug = routePath.startsWith('blog/') && routePath !== 'blog'
    ? routePath.replace('blog/', '')
    : null;
  const routeFaqs = useMemo(
    () => resolveFaqsForPathname(location.pathname, i18n.language),
    [location.pathname, i18n.language],
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <OrganizationJsonLd />
      <BreadcrumbJsonLd />
      {routeFaqs?.length ? <FAQPageJsonLd faqs={routeFaqs} /> : null}
      {blogSlug && <BlogPostingJsonLd slug={blogSlug} />}
      <Header />
      {/* One boundary for the whole main column: route content, the visible FAQ
          section and the footer arrive atomically. The nightly CLS audit
          measured 0.50 on every lazy route because the FAQ section and footer
          painted in the shell and were then shoved down by the arriving lazy
          chunk (home was exempt only because Index is eager) — anything that
          paints early must not be movable late.
          CWS-5: the error boundary below is NOT a Suspense boundary — it does
          not capture suspension, so the atomic-render contract is unchanged;
          it only contains a render throw to the main column, keeping the
          header, footer and language switcher alive. resetKey clears a failed
          state on navigation; a healthy navigation re-renders in place. */}
      <Suspense fallback={<div className="min-h-[40vh]" aria-hidden="true" />}>
        <RenderErrorBoundary
          surface="page"
          resetKey={location.pathname}
          fallback={<PageErrorFallback />}
        >
          <main className="flex-1">
            <Outlet />
            {routeFaqs?.length ? <VisibleFaqSection faqs={routeFaqs} /> : null}
          </main>
        </RenderErrorBoundary>
        <Footer />
      </Suspense>
    </div>
  );
}

function setMeta(name: string, content: string, isProperty = false, attr = isProperty ? 'property' : 'name') {
  const actualAttr = isProperty ? 'property' : attr;
  const selector = `meta[${actualAttr}="${name}"]`;
  let el = document.querySelector(selector);
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(actualAttr, name);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

function replaceMetaList(name: string, contents: string[], attr: "name" | "property") {
  document
    .querySelectorAll(`meta[${attr}="${name}"]`)
    .forEach((element) => element.remove());

  for (const content of contents) {
    const el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("content", content);
    document.head.appendChild(el);
  }
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (el) {
    el.href = href;
  } else {
    el = document.createElement('link');
    el.rel = rel;
    el.href = href;
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
}

export function useLanguageNavigation() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const changeLanguage = (lang: string) => {
    // The same classifier the rescue effect calls (CWS-62): an unsupported
    // two-letter prefix is a prefix, not a path segment, so switching language
    // strips it instead of prepending in front of it.
    const prefix = classifyUrlLanguagePrefix(location.pathname, SUPPORTED_LANGS);
    const newPath = `/${lang}${prefix.pathWithoutPrefix}${location.search}${location.hash}`;
    // CWS-199: an already-registered locale takes the synchronous fast path
    // inside requestLanguageChange (identical to the pre-CWS-199 sequence);
    // a lazy one is chained behind its chunk load and refuses the switch —
    // staying on the current language, URL untouched — if the chunk fails to
    // load: the FAQ resolver throws for an unregistered language, and though
    // CWS-5's router-level boundary contains that throw, "switch anyway"
    // would still trade the whole page for the plain fallback.
    requestLanguageChange(i18n, lang, () => {
      persistManualLanguage(normalizeSeoLanguage(lang));
      navigate(newPath);
    });
  };

  const getLocalizedPath = (path: string) => {
    const lang = i18n.language?.split('-')[0] || 'en';
    const safeLang = SUPPORTED_LANGS.includes(lang as AppLanguage) ? lang : 'en';
    
    // Remove any existing language prefix
    const cleanPath = path.replace(/^\/(en|de|fr|it|es)(?=\/|$)/, '');
    // Through the normaliser rather than an inline slash-collapsing chain: an
    // internal link that disagreed with the canonical form would point every
    // crawler at a redirect, which is the defect B3 exists to remove.
    return normalizeUrlPath(`/${safeLang}${cleanPath}`);
  };

  return { changeLanguage, getLocalizedPath, currentLanguage: i18n.language };
}

/**
 * The page-level error boundary's fallback — an apology plus a way onward
 * (CWS-5).
 *
 * Lives here rather than in ``RenderErrorBoundary.tsx`` so it can use
 * ``useTranslation`` and ``useLanguageNavigation`` without an import cycle:
 * the router imports the boundary class, and this fallback, from the same
 * module that defines the layout. The owner chose apology + links over a
 * redirect (a redirect erases the failing URL) and over a dead-end inline
 * message (2026-09-19, on the ticket).
 *
 * The links are plain anchors, on purpose: a full reload is the cleanest
 * recovery from a render failure that may have corrupted in-memory state,
 * and it is what ``NotFound`` already does. Contact Us opens the real
 * contact modal — the conversion path the ticket's framing says a contained
 * failure must not cost — and degrades to a warned no-op outside its
 * provider, so the fallback itself can never throw for context's absence.
 *
 * Its root is a ``<main>``: the boundary wraps the route's ``<main>`` element
 * itself, so in the failure state this element *is* the page's main — keeping
 * the landmark and the ``flex-1`` height instead of demoting the body to a
 * generic section and letting the footer drift up.
 */
export function PageErrorFallback() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const { openContactForm } = useContactForm();

  return (
    <main className="flex min-h-[60vh] flex-1 items-center justify-center px-4 py-16">
      <div className="max-w-md text-center">
        <h1 className="mb-4 text-3xl font-bold">{t('errorBoundary.title')}</h1>
        <p className="mb-6 text-muted-foreground">{t('errorBoundary.description')}</p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={getLocalizedPath('/')}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t('errorBoundary.backHome')}
          </a>
          <a
            href={getLocalizedPath('/services/consulting')}
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            {t('errorBoundary.viewServices')}
          </a>
          <button
            type="button"
            onClick={() => openContactForm({ page: 'Error Fallback', button: 'Contact Us' })}
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            {t('errorBoundary.contactUs')}
          </button>
        </div>
      </div>
    </main>
  );
}
